import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '@whinerp/client';
import { Button, Panel } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';

/**
 * Logging a case that arrived by phone or in person (G-193). Chat, email and
 * the portal make their own; this is for the channels with no system behind
 * them. Same queue, same SLA clock.
 */

const INPUT = 'h-input rounded-control border border-control bg-surface px-2 text-body text-ink';

export function NewCasePanel(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ subject: '', body: '', source: 'phone' as 'phone' | 'manual' | 'email' });
  const create = useMutation({
    mutationFn: () => api.createCase({ body: { subject: draft.subject, body: draft.body || null, source: draft.source } }),
    onSuccess: () => {
      setDraft({ subject: '', body: '', source: 'phone' });
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['case-queue'] });
    },
  });
  if (!open) {
    return (
      <div>
        <Button size="small" data-new-case onClick={() => setOpen(true)}>{t('case.new')}</Button>
      </div>
    );
  }
  return (
    <Panel data-region="new-case">
      <form className="flex flex-col gap-2" data-new-case-form
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-1 flex-col gap-1 text-meta text-ink-meta">
            {t('case.subject')}
            <input className={INPUT} required maxLength={200} data-new-case-subject value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('case.cameIn')}
            <select className={INPUT} data-new-case-source value={draft.source} onChange={(event) => setDraft({ ...draft, source: event.target.value as 'phone' })}>
              {(['phone', 'manual', 'email'] as const).map((source) => <option key={source} value={source}>{t(`case.source.${source}`)}</option>)}
            </select>
          </label>
        </div>
        <label className="flex flex-col gap-1 text-meta text-ink-meta">
          {t('case.whatTheySaid')}
          <textarea className="min-h-16 rounded-control border border-control bg-surface p-2 text-body text-ink" data-new-case-body value={draft.body}
            onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
        </label>
        {create.error ? <p className="text-dense text-danger-text">{errorText(create.error as ApiError)}</p> : null}
        <div className="flex gap-2">
          <Button intent="primary" size="small" type="submit" data-new-case-save disabled={create.isPending}>{t('case.log')}</Button>
          <Button size="small" onClick={() => setOpen(false)}>{t('action.cancel')}</Button>
        </div>
      </form>
    </Panel>
  );
}
